import { useState, useEffect } from 'react';
import s from './styles.module.scss';
import pencilIcon from '../../assets/pencil.svg';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TaskItem = ({ task, onToggleStatus, onSave, courseColor, variant = 'widget' }) => {
  const [isChecked, setIsChecked] = useState(task.status === 'done');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, opacity) => {
    if (!hex) return `rgba(0, 0, 0, ${opacity})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Determine styles based on variant and expanded state
  const getTaskStyles = () => {
    if (variant === 'widget') {
      // Widget variant: always use course color for border and background
      return {
        border: `1px solid ${courseColor || 'rgba(0, 0, 0, 0.45)'}`,
        backgroundColor: hexToRgba(courseColor || '#000000', 0.05)
      };
    } else {
      // Course variant: white background, border changes on expand
      return {
        border: `1px solid ${isExpanded ? (courseColor || 'rgba(0, 0, 0, 0.45)') : 'rgba(0, 0, 0, 0.45)'}`,
        backgroundColor: 'white'
      };
    }
  };

  // Edit state
  const [editedTitle, setEditedTitle] = useState(task.title);
  const [editedDescription, setEditedDescription] = useState(task.description || '');
  const [editedDueAt, setEditedDueAt] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Initialize date/time from task.due_at
  useEffect(() => {
    if (task.due_at) {
      setEditedDueAt(new Date(task.due_at));
    } else {
      setEditedDueAt(null);
    }
  }, [task.due_at]);

  const handleToggle = async () => {
    setIsAnimating(true);
    const newStatus = isChecked ? 'open' : 'done';
    setIsChecked(!isChecked);

    try {
      await onToggleStatus(task.id, newStatus);
    } catch (error) {
      setIsChecked(isChecked);
      console.error('Failed to update task status:', error);
    } finally {
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const validate = () => {
    const newErrors = {};

    // Title validation
    if (!editedTitle.trim()) {
      newErrors.title = 'Title is required';
    } else if (editedTitle.length > 255) {
      newErrors.title = 'Title must be 255 characters or less';
    }

    // Description validation
    if (editedDescription && editedDescription.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less';
    }

    // Due date validation
    if (editedDueAt) {
      const now = new Date();
      if (editedDueAt < now) {
        newErrors.due_at = 'Due date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.stopPropagation();

    if (!validate()) {
      return;
    }

    try {
      const dueAtISO = editedDueAt ? editedDueAt.toISOString() : null;

      const updatedData = {
        title: editedTitle,
        description: editedDescription,
        due_at: dueAtISO
      };

      await onSave(task.id, updatedData);
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    // Revert to original values
    setEditedTitle(task.title);
    setEditedDescription(task.description || '');
    if (task.due_at) {
      setEditedDueAt(new Date(task.due_at));
    } else {
      setEditedDueAt(null);
    }
    setIsEditing(false);
    setErrors({});
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Get weekday name
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });

    // Get month/day (e.g., "11/10")
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dateStr = `${month}/${day}`;

    // Special cases: Today and Tomorrow
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${dateStr}`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${dateStr}`;
    }

    // Default: Weekday, Date
    return `${weekday}, ${dateStr}`;
  };

  return (
    <div
      className={`${s.taskItem} ${isChecked ? s.completed : ''} ${isAnimating ? s.animating : ''} ${isExpanded ? s.expanded : ''}`}
      style={getTaskStyles()}
    >
      <button
        className={`${s.checkbox} ${isChecked ? s.checked : ''}`}
        onClick={handleToggle}
        aria-label={isChecked ? 'Mark as incomplete' : 'Mark as complete'}
      >
        {isChecked && (
          <svg viewBox="0 0 16 16" className={s.checkIcon}>
            <path d="M13.5 3L6 10.5L2.5 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      <div className={s.taskContent} onClick={handleExpand}>
        {!isEditing ? (
          <>
            <div className={s.taskHeader}>
              <span className={s.taskTitle}>{task.title}</span>
              <div className={s.taskIcons}>
                {isExpanded && (
                  <button
                    className={s.editButton}
                    onClick={handleEdit}
                    aria-label="Edit task"
                  >
                    <img src={pencilIcon} alt="Edit" className={s.editIcon} />
                  </button>
                )}
                <svg
                  viewBox="0 0 16 16"
                  className={`${s.chevron} ${isExpanded ? s.chevronExpanded : ''}`}
                >
                  <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            {task.due_at && (
              <span className={s.taskTime}>
                {formatDate(task.due_at)} at {formatTime(task.due_at)}
              </span>
            )}
            {isExpanded && task.description && (
              <div className={s.taskDescription}>
                {task.description}
              </div>
            )}
          </>
        ) : (
          <div className={s.editMode} onClick={(e) => e.stopPropagation()}>
            <div className={s.editField}>
              <label htmlFor={`title-${task.id}`}>Title</label>
              <input
                id={`title-${task.id}`}
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className={errors.title ? s.inputError : ''}
                placeholder="Task title"
              />
              {errors.title && <span className={s.errorText}>{errors.title}</span>}
            </div>

            <div className={s.editField}>
              <label htmlFor={`description-${task.id}`}>Description</label>
              <textarea
                id={`description-${task.id}`}
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className={errors.description ? s.inputError : ''}
                placeholder="Task description"
                rows={3}
              />
              {errors.description && <span className={s.errorText}>{errors.description}</span>}
            </div>

            <div className={s.editField}>
              <label htmlFor={`due-at-${task.id}`}>Due Date & Time</label>
              <DatePicker
                id={`due-at-${task.id}`}
                selected={editedDueAt}
                onChange={(date) => setEditedDueAt(date)}
                showTimeSelect
                timeFormat="h:mm aa"
                timeIntervals={15}
                dateFormat="MMM d, yyyy h:mm aa"
                className={errors.due_at ? s.inputError : ''}
                placeholderText="Select due date and time"
                isClearable
              />
              {errors.due_at && <span className={s.errorText}>{errors.due_at}</span>}
            </div>

            <div className={s.editActions}>
              <button className={s.cancelButton} onClick={handleCancel}>
                Cancel
              </button>
              <button className={s.saveButton} onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskItem;
